import React from 'react';
import { Alert, Field } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import type { InstagramAudioSelection } from 'types/Attachments';
import InstagramAudioConnectProfileNotice from './InstagramAudioConnectProfileNotice';
import InstagramAudioEmptyState from './InstagramAudioEmptyState';
import InstagramAudioSummary from './InstagramAudioSummary';

interface InstagramAudioFieldProps {
  value: InstagramAudioSelection | null;
  pageId: number;
  videoSrc: string | null;
  videoDurationMs: number | null;
  isDisabled?: boolean;
  disabledReason?: string | null;
  unavailableNotice?: string | null;
  isManuallyPublished?: boolean;
  isProfileDisconnected: boolean;
  canPromptConnection: boolean;
  onOpenPicker: () => void;
  onChange: (value: InstagramAudioSelection | null) => void;
}

const InstagramAudioField = ({
  value,
  pageId,
  videoSrc,
  videoDurationMs,
  isDisabled,
  disabledReason,
  unavailableNotice,
  isManuallyPublished,
  isProfileDisconnected,
  canPromptConnection,
  onOpenPicker,
  onChange,
}: InstagramAudioFieldProps) => {
  const { t } = useTranslation();

  const canShowAudioControl = !!value || !isProfileDisconnected;
  const lockedReason = isDisabled ? disabledReason : null;

  return (
    <Field.Group>
      <Field.Label>{t('instagramAudio.label')}</Field.Label>

      {unavailableNotice && <Alert variant="info">{unavailableNotice}</Alert>}

      {isProfileDisconnected && (
        <InstagramAudioConnectProfileNotice pageId={pageId} />
      )}

      {isManuallyPublished && (
        <Alert variant="warning">{t('instagramAudio.mobileAppWarning')}</Alert>
      )}

      {canShowAudioControl && (
        <div data-name="instagram-audio_field">
          {value ? (
            <InstagramAudioSummary
              value={value}
              pageId={pageId}
              videoSrc={videoSrc}
              videoDurationMs={videoDurationMs}
              isDisabled={isDisabled}
              disabledReason={lockedReason}
              isLibraryAvailable={!unavailableNotice}
              canPromptConnection={
                canPromptConnection && !isProfileDisconnected
              }
              onChange={onChange}
              onRemove={() => onChange(null)}
              onOpenPicker={onOpenPicker}
            />
          ) : (
            <InstagramAudioEmptyState
              isDisabled={isDisabled || !!unavailableNotice}
              disabledReason={lockedReason}
              onOpenPicker={onOpenPicker}
            />
          )}
        </div>
      )}

      {value && (
        <Field.Caption data-cy="instagram-audio_availability-note">
          {t('instagramAudio.availabilityNote')}
        </Field.Caption>
      )}
    </Field.Group>
  );
};

export default InstagramAudioField;
